import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, type ApiError, type GetFlowResult, type ListPalettesResult } from '@whinerp/client';
import {
  Button,
  EmptyState,
  ErrorState,
  FlowCanvas,
  Icon,
  PageHeading,
  Panel,
  SectionLabel,
  Skeleton,
  useToast,
} from '@whinerp/ui';
import type { FlowGraph, Palette } from '@whinerp/contracts/flow';
import { useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { flowCanvasLabels } from './flow-labels.js';

/**
 * SC-40 Chatbot script builder.
 *
 * The fourth caller of the one canvas, given the chat palette. Publishing is
 * blocked by the same validator the automation builder uses, so an unreachable
 * node is caught here for the same reason and with the same words - rather
 * than being discovered by a visitor whose conversation stopped.
 */

function ChatbotBuilderScreen(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const params = useParams();
  const flowId = params['id'] ?? '';

  const [graph, setGraph] = useState<FlowGraph | null>(null);

  const flow = useQuery<GetFlowResult, ApiError>({
    queryKey: ['flow', flowId],
    queryFn: () => api.getFlow({ path: { flowId } }),
  });

  const palettes = useQuery<ListPalettesResult, ApiError>({
    queryKey: ['palettes'],
    queryFn: () => api.listPalettes({}),
  });

  useEffect(() => {
    if (flow.data && graph === null) setGraph(flow.data.graph as FlowGraph);
  }, [flow.data, graph]);

  const publish = useMutation({
    mutationFn: () =>
      api.saveFlow({
        path: { flowId },
        body: { graph: (graph ?? { nodes: [], edges: [] }) as never, status: 'active' },
      }),
    onSuccess: () => {
      toast.show({ tone: 'success', title: t('chatbot.publish') });
      void flow.refetch();
    },
    onError: (error: ApiError) =>
      toast.show({ tone: 'error', title: t('chatbot.blocked'), body: error.message }),
  });

  if (flow.isError) return <ChatbotError error={flow.error} retry={() => void flow.refetch()} />;
  if (flow.isPending || !graph) return <ChatbotLoading />;

  const palette = (palettes.data?.palettes.find((entry) => entry.key === flow.data.palette) ??
    null) as Palette | null;
  if (!palette) return <ChatbotLoading />;

  const problems = flow.data.problems;

  return (
    <div data-component="ChatbotBuilder" className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-center gap-3">
        <PageHeading>{flow.data.name}</PageHeading>
        <Button
          intent="primary"
          data-publish
          className="ms-auto"
          disabled={problems.length > 0 || publish.isPending}
          onClick={() => publish.mutate()}
        >
          {t('chatbot.publish')}
        </Button>
      </header>

      {problems.length > 0 ? (
        <Panel data-region="blocked">
          <div className="flex flex-col gap-1">
            <SectionLabel>{t('chatbot.blocked')}</SectionLabel>
            <ul className="flex flex-col">
              {problems.map((problem) => (
                <li
                  key={`${problem.nodeId ?? 'flow'}-${problem.message}`}
                  data-publish-problem
                  className="flex items-center gap-1 text-dense text-danger-text"
                >
                  <Icon name="Warning" size="inline" decorative />
                  {problem.message}
                </li>
              ))}
            </ul>
          </div>
        </Panel>
      ) : null}

      <Panel data-region="canvas">
        <FlowCanvas
          graph={graph}
          palette={palette}
          labels={flowCanvasLabels(t)}
          problems={problems}
          onChange={setGraph}
        />
      </Panel>
    </div>
  );
}

function ChatbotEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="ChatText"
      title={t('chatbot.title')}
      body={t('flow.empty.body')}
      primary={{ label: t('empty.action.addStep'), icon: 'Plus', href: '/chat' }}
    />
  );
}

function ChatbotLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-lg" />
      <Skeleton className="h-block-2xl w-full" />
    </div>
  );
}

function ChatbotError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="This script could not be loaded"
      body={error instanceof Error ? error.message : 'It did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-40',
  route: '/chat/bots/:id',
  surface: 'page',
  title: 'Chatbot script builder',
  // F-068, not F-151. This screen is the visual builder given the chat
  // palette; the live chat widget F-151 describes has nothing behind it yet,
  // and claiming it would claim a capability a visitor cannot reach.
  features: ['F-068'],
  entitlement: 'F-068',
  reachedBy: [{ kind: 'setup', section: 'chat' }],
  components: ['FlowCanvas'],
  elements: ['ChatbotBuilder'],
  sampleRecord: { objectKey: 'flow', param: 'id' },
  states: {
    empty: ChatbotEmpty,
    loading: ChatbotLoading,
    error: ChatbotError,
  },
  component: ChatbotBuilderScreen,
});

export default screen;
